import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface BaseHrLevelFamilyDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    uniqueKey: string;
    referenceYear_Id : number;
    baseHrLevel_Ids: number[];
    nbObjectiveBottomThreshold: number;
    nbObjectiveTopThreshold: number;
}

export interface BaseHrLevelFamilyState {
    baseHrLevelFamilies: BaseHrLevelFamilyDto[];
    baseHrLevelFamily: BaseHrLevelFamilyDto;
    gettingBaseHrLevelFamilies: boolean;
    gettingBaseHrLevelFamily: boolean;
    archivingBaseHrLevelFamily: boolean;
    showDeleteBaseHrLevelFamilyModal: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    statusGetAllFamilies: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankBaseHrLevelFamilyDto: BaseHrLevelFamilyDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    uniqueKey: '',
    referenceYear_Id: 0,
    baseHrLevel_Ids: [],
    nbObjectiveBottomThreshold: 0,
    nbObjectiveTopThreshold: 0,
};

const initialState: BaseHrLevelFamilyState = {
    baseHrLevelFamilies: [],
    baseHrLevelFamily: blankBaseHrLevelFamilyDto,
    gettingBaseHrLevelFamilies: false,
    gettingBaseHrLevelFamily: false,
    archivingBaseHrLevelFamily: false,
    showDeleteBaseHrLevelFamilyModal: false,
    status: 'idle',
    statusGetAllFamilies: 'idle',
    error: undefined,
};

export const baseHrLevelFamilySlice = createSlice({
    name: 'baseHrLevelFamilies',
    initialState,
    reducers: {
        clearBaseHrLevelFamilies: (state) => {
            state.baseHrLevelFamilies = [];
            state.baseHrLevelFamily = blankBaseHrLevelFamilyDto;
            state.statusGetAllFamilies = 'idle';
            state.status = 'idle';
        },
        updateBaseHrLevelFamily: (state, param) => {
            const { payload } = param;
            state.baseHrLevelFamily = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getBaseHrLevelFamilies.pending, (state, action) => {
                state.gettingBaseHrLevelFamilies = true;
                state.statusGetAllFamilies = 'loading';
            })
            .addCase(getBaseHrLevelFamilies.fulfilled, (state, action) => {
                state.gettingBaseHrLevelFamilies = false;
                state.baseHrLevelFamilies = action.payload;
                state.statusGetAllFamilies = 'succeeded';
            })
            .addCase(getBaseHrLevelFamilies.rejected, (state, action) => {
                state.gettingBaseHrLevelFamilies = false;
                state.baseHrLevelFamilies = [];
                state.statusGetAllFamilies = 'failed';
                state.error = action.error.message;
            })
            .addCase(getBaseHrLevelFamily.pending, (state, action) => {
                state.gettingBaseHrLevelFamily = true;
                state.status = 'loading';
            })
            .addCase(getBaseHrLevelFamily.fulfilled, (state, action) => {
                state.gettingBaseHrLevelFamily = false;
                state.baseHrLevelFamily = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getBaseHrLevelFamily.rejected, (state, action) => {
                state.gettingBaseHrLevelFamily = false;
                state.baseHrLevelFamily = blankBaseHrLevelFamilyDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveBaseHrLevelFamily.pending, (state, action) => {
                state.archivingBaseHrLevelFamily = true;
                state.status = 'loading';
            })
            .addCase(archiveBaseHrLevelFamily.fulfilled, (state, action) => {
                state.archivingBaseHrLevelFamily = false;
                state.baseHrLevelFamilies = state.baseHrLevelFamilies.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveBaseHrLevelFamily.rejected, (state, action) => {
                state.archivingBaseHrLevelFamily = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearBaseHrLevelFamilies } = baseHrLevelFamilySlice.actions;
export const { updateBaseHrLevelFamily } = baseHrLevelFamilySlice.actions;

export const getBaseHrLevelFamilies = createAsyncThunk('base-hrlevel-families/getBaseHrLevelFamilies', async (referenceYearId:number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/base-hrlevel-families/getBaseHrLevelFamilies/${referenceYearId}`);

    return response.data;
});

export const getBaseHrLevelFamily = createAsyncThunk('base-hrlevel-families/getBaseHrLevelFamily', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/base-hrlevel-families/${id}`);

    return response.data;
});

export const archiveBaseHrLevelFamily = createAsyncThunk('base-hrlevel-families/archiveBaseHrLevelFamily', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/base-hrlevel-families/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedBaseHrLevelFamily'),
        })
    );
    return response.data;
});

export const saveBaseHrLevelFamily = createAsyncThunk(
    'base-hrlevel-families/saveBaseHrLevelFamily',
    async (baseHrLevelFamilyToSave: BaseHrLevelFamilyDto, thunkAPI) => {
        if (baseHrLevelFamilyToSave.id === 0) {
            const response = await axios.post(`/api/synergy/base-hrlevel-families/create`, baseHrLevelFamilyToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('createBaseHrLevelFamily'),
                })
            );
            return response.data;
        } else {
            const response = await axios.put(`/api/synergy/base-hrlevel-families/put`, baseHrLevelFamilyToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('saveBaseHrLevelFamily'),
                })
            );
            return response.data;
        }
    }
);
